import { useMemo, useState } from 'react';
import { useAuth } from 'context/auth';
import { useMeQuery } from 'hooks/api/users';
import { Loader } from 'common';
import { Anchor } from 'constants/anchors';
import { ICasino, ICasinoReview, IStreamer } from 'constants/types';
import { Button } from 'common/new-button';
import { Icon } from 'common/icon';
import { RedirectActions } from 'constants/redirect-actions';
import { useLoginModal, useRedirectActions } from 'hooks';
import { LoginModal } from 'components/modal/login';
import { saveRedirectOptions } from 'utils/redirectOptions';
import { CasinoReviewModal } from 'components/modal/casino-review';
import { Routes } from 'constants/routes';
import styles from './styles.module.css';
import { getItemIdentifier } from 'utils/slug';
import { Header } from 'components/slider/header';
import { ReviewsMasonry } from 'components/reviews-masonry';
import { Analytics } from 'constants/analytics';
import { ReviewsCard } from 'components/cards';

function isCasino(entity: ICasino | IStreamer): entity is ICasino {
  return (entity as ICasino).slots_count !== undefined;
}

export const TableCommentsCasino = ({
  data,
  count,
  entity,
  isInfiniti,
  lastItemRef,
  hasHeader,
  type,
  onSuccess,
}: {
  entity: ICasino | IStreamer;
  idCasino?: string;
  lastItemRef?: any;
  isInfiniti?: boolean;
  count: string | number;
  isSeeMoreVisible?: boolean;
  data: ICasinoReview[];
  hasHeader?: boolean;
  type: 'casino' | 'streamer';
  onSuccess?: () => void;
}) => {
  const { data: me } = useMeQuery();
  const { isLoggedIn } = useAuth();
  const [isReviewVisible, setReviewVisible] = useState(false);
  const reviewTitle = `${isCasino(entity) ? entity?.name : entity?.username} ${
    isCasino(entity) ? 'casino reviews' : 'streamer reviews'
  }`;

  const isReviewExist = useMemo(() => {
    if (me?.user) {
      if (me.user.reviews && me.user.reviews.length > 0) {
        return me.user.reviews.find((review: ICasinoReview) => {
          return review.casino_id === entity?._id;
        });
      }
    }
    return null;
  }, [entity?._id, me?.user]);

  const reviewActionText = isReviewExist
    ? 'Edit casino review'
    : 'Add casino review';
  const renderActionButton = () => (
    <Button
      id={
        isReviewExist
          ? Analytics.casino.inspectEditReview
          : Analytics.casino.inspectAddReview
      }
      icon={<Icon.MessageCommentAdd />}
      text={reviewActionText}
      onClick={() => {
        if (isLoggedIn) {
          setReviewVisible(true);
        } else {
          saveRedirectOptions({
            url:
              Routes.casinoPage(getItemIdentifier(entity)) +
              `#${Anchor.Inspect}`,
            action: RedirectActions.WriteCasinoReview,
          });
          handleVisibleLoginModal();
        }
      }}
      className={styles.casino_review__action}
    />
  );
  const {
    handleCloseLoginModal,
    handleConfirmLoginModal,
    handleVisibleLoginModal,
    isLoginVisible,
  } = useLoginModal();

  useRedirectActions({
    actions: [
      {
        name: RedirectActions.WriteCasinoReview,
        action: () => setReviewVisible(true),
      },
    ],
  });
  return (
    <div className={styles.reviews}>
      <div className={styles.container_s}>
        <div className={styles.action}>
          <div className={styles.header}>
            {hasHeader && (
              <Header
                isBigName={type ? true : false}
                id={Analytics.casino.allReviews}
                hasIcon={false}
                count={count?.toString()}
                name={reviewTitle}
                isSmallName
              />
            )}
          </div>
          {type && !!data?.length && (
            <div className={styles.mobileActionButton}>
              {renderActionButton()}
            </div>
          )}
        </div>
        <LoginModal
          isVisible={isLoginVisible}
          onClose={handleCloseLoginModal}
          confirmOnClick={handleConfirmLoginModal}
        />
        <CasinoReviewModal
          onSuccess={onSuccess}
          casino={entity as ICasino}
          isVisible={isReviewVisible}
          setVisible={setReviewVisible}
        />
        <ReviewsMasonry
          gap="20px"
          actionButton={
            type &&
            !!data?.length && (
              <div className={styles.desktopMasonryAction}>
                {renderActionButton()}
              </div>
            )
          }
        >
          {!data?.length ? (
            <div key="no-reviews" className={styles.noReviewsFound}>
              <div>
                No player reviews yet. Be the first to share your experience!
              </div>
              {type && (
                <Button
                  id={Analytics.casino.inspectAddReview}
                  icon={<Icon.MessageCommentAdd />}
                  text={reviewActionText}
                  onClick={() => {
                    if (isLoggedIn) {
                      setReviewVisible(true);
                    } else {
                      saveRedirectOptions({
                        url:
                          Routes.casinoPage(getItemIdentifier(entity)) +
                          `#${Anchor.Inspect}`,
                        action: RedirectActions.WriteCasinoReview,
                      });
                      handleVisibleLoginModal();
                    }
                  }}
                  className={styles.casino_review__action}
                />
              )}
            </div>
          ) : (
            data.map((item) => {
              return (
                <ReviewsCard
                  key={item?._id?.toString()}
                  comment={item}
                  variant="panel"
                />
              );
            })
          )}
        </ReviewsMasonry>
        {isInfiniti && <Loader template="fitContent" />}
        <div ref={lastItemRef} />
      </div>
    </div>
  );
};
